"use client";

/**
 * Vertical quick-nav rail on the left of the /products page.
 *
 * Visibility: MOBILE + TABLET only (< md, i.e. below 768px). Desktop users
 * have the FilterSidebar so the rail would duplicate navigation — hidden
 * via `md:hidden`.
 *
 * Design notes:
 *   - Sticky within the page container so it stays visible while the
 *     product grid scrolls. The top offset is computed the SAME way as the
 *     storefront layout's `main` padding —
 *     `calc(4rem + var(--banner-h,0px) + env(safe-area-inset-top) + 0.5rem)`
 *     — never a static `top-24`. The header is `fixed` and its height is
 *     variable: `--banner-h` is measured from the live AnnouncementBar and
 *     pushed onto `<html>`, and `viewportFit: "cover"` makes the safe-area
 *     inset non-zero in ordinary mobile Safari on a notched iPhone. A
 *     hardcoded 96px hid the "Shop by" label and the first tab behind the
 *     header (and most of the rail when a banner wrapped to two lines).
 *   - Card-style container: `bg-card`, `border-2`, shadow, padding — so
 *     it's obviously a separate nav surface, not floating tabs.
 *   - Each category tab has a SEMANTIC accent colour + the selected tab gets a
 *     filled pill + left "you are here" bar (see ProductQuickNavTab).
 *   - A synthetic "All" tab leads the rail and is selected by default on
 *     landing (when no `?tab=` is set), so the rail always shows a clear
 *     active tab without hiding any of the catalogue.
 *   - Full ARIA tablist with arrow-key navigation (Home/End jump to the
 *     ends; Up/Down move one tab; wraps at both ends).
 *   - iOS safe-area insets on the left and bottom.
 *
 * Click behaviour: tabs toggle `?tab=<slug>` on the URL; "All" (and tapping the
 * active tab again) clears it. Other query params (sort, pagination) persist.
 *
 * ── QNAVLINK: every tab is a REAL LINK ──────────────────────────────────────
 * Because that behaviour is entirely a function of the current query string, the
 * target URL is derivable at render time — so each tab is a `next/link` with a
 * genuine href instead of a `<button>`. Before this the rail shipped zero
 * crawlable hrefs on `/products`. `<Link replace scroll={false}>` reproduces the
 * old `router.replace(target, { scroll: false })` exactly, so there is no
 * behavioural change beyond the tag; see `ProductQuickNavTab`.
 */

import { useCallback, useEffect, useMemo, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useProductNavTabs, type PublicNavTab } from "@/hooks/useProductNavTabs";
import { ProductQuickNavTab } from "./ProductQuickNavTab";

/**
 * Synthetic "All" tab. Prepended to the rail so it always has a clear default
 * selection on landing — active whenever no `?tab=` is set; tapping it clears
 * the tab filter (shows the whole catalogue, hiding nothing). Not stored in the
 * DB; only rendered when admin-configured tabs exist.
 */
const ALL_TAB_SLUG = "__all__";
const ALL_TAB: PublicNavTab = {
  slug: ALL_TAB_SLUG,
  label: "All",
  iconName: "LayoutGrid",
  sortOrder: -1,
};

export interface ProductQuickNavProps {
  /**
   * Id of the product grid section this rail controls. Used for
   * `aria-controls` on the tablist so assistive tech connects the two.
   */
  controlsId?: string;
  /**
   * Active collection scope. When set, the rail fetches tabs scoped to
   * this collection (or falls back to global tabs filtered to the
   * collection's products). When null/undefined, the rail uses the
   * global tab list.
   */
  scope?: string | null;
  className?: string;
}

export function ProductQuickNav({
  controlsId = "product-grid",
  scope,
  className,
}: ProductQuickNavProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { tabs, activeSlug, loading } = useProductNavTabs({ scope });

  // Prepend "All" so the rail always has a default-selected tab on landing.
  // Only when there are real tabs — a lone "All" would be pointless.
  const railTabs = useMemo<PublicNavTab[]>(
    () => (tabs.length > 0 ? [ALL_TAB, ...tabs] : []),
    [tabs]
  );

  // One ref per rendered tab so arrow-key handlers can call .focus().
  const tabRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  useEffect(() => {
    tabRefs.current = tabRefs.current.slice(0, railTabs.length);
  }, [railTabs.length]);

  /**
   * The crawlable target for one tab — the whole reason each tab can be a real
   * link. Identical arithmetic to the `router.replace` this replaced:
   *   • "All" clears `?tab=`;
   *   • tapping the ACTIVE category again also clears it (tap-to-toggle);
   *   • anything else sets `?tab=<slug>`;
   *   • `?page=` is always dropped, or page 3 of one filter persists into the
   *     next; every other param (sort, colour, …) survives.
   */
  const hrefForSlug = useCallback(
    (slug: string) => {
      const params = new URLSearchParams(searchParams.toString());
      if (slug === ALL_TAB_SLUG || slug === activeSlug) params.delete("tab");
      else params.set("tab", slug);
      params.delete("page");
      const qs = params.toString();
      return qs ? `${pathname}?${qs}` : pathname;
    },
    [pathname, searchParams, activeSlug]
  );

  const focusTab = useCallback((index: number) => {
    const node = tabRefs.current[index];
    if (node) node.focus();
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLAnchorElement>) => {
      const target = e.currentTarget;
      const indexAttr = target.getAttribute("data-index");
      const currentIndex = indexAttr ? Number.parseInt(indexAttr, 10) : 0;
      const count = railTabs.length;
      if (count === 0) return;

      switch (e.key) {
        case "ArrowDown":
        case "ArrowRight": {
          e.preventDefault();
          focusTab((currentIndex + 1) % count);
          break;
        }
        case "ArrowUp":
        case "ArrowLeft": {
          e.preventDefault();
          focusTab((currentIndex - 1 + count) % count);
          break;
        }
        case "Home": {
          e.preventDefault();
          focusTab(0);
          break;
        }
        case "End": {
          e.preventDefault();
          focusTab(count - 1);
          break;
        }
        case "Enter": {
          // Native anchor activation already navigates — nothing to intercept.
          break;
        }
        case " ": {
          // An anchor is NOT activated by Space (it scrolls the page instead),
          // but the ARIA tabs pattern requires Space to select — so suppress the
          // scroll and synthesise the activation the link already knows how to do.
          e.preventDefault();
          e.currentTarget.click();
          break;
        }
        default:
          break;
      }
    },
    [railTabs.length, focusTab]
  );

  const indexedTabs = useMemo(
    () =>
      railTabs.map((t, i) => ({
        ...t,
        index: i,
        // "All" is active when no specific tab is selected; otherwise the tab
        // whose slug matches the URL is active.
        active: t.slug === ALL_TAB_SLUG ? activeSlug === null : t.slug === activeSlug,
        href: hrefForSlug(t.slug),
      })),
    [railTabs, activeSlug, hrefForSlug]
  );

  // No empty rail — if the admin hasn't configured any tabs, render nothing.
  if (!loading && tabs.length === 0) return null;
  // During the first fetch we reserve the horizontal space so the grid
  // doesn't reflow when the rail lands; but no skeleton content inside
  // (reduces visual noise on fast loads). Mobile-only to match the rail.
  if (loading) {
    return (
      <div
        aria-hidden="true"
        className={[
          // Reserve the rail's slot at every mobile size. The width mirrors
          // the rendered rail (80px on the smallest phones, 88px from
          // ~400px up) so the grid doesn't reflow when the rail lands.
          "w-20 min-[400px]:w-[5.5rem] shrink-0 md:hidden",
          className ?? "",
        ].join(" ")}
      />
    );
  }

  return (
    <aside
      className={[
        // Mobile/tablet only — desktop uses the FilterSidebar instead.
        "md:hidden",
        // Dynamic width: 80px on phones below 400px, 88px from ~400px up.
        // Sized so the longest SINGLE word label ("Accessories") fits on one
        // line at the 10–11px label size (words never break mid-word — see
        // ProductQuickNavTab); multi-word labels split per word. Still leaves
        // the product grid room to breathe (the grid is 1-column on mobile).
        "shrink-0 w-20 min-[400px]:w-[5.5rem]",
        // Stick within the page container, clearing the site header. The
        // offset MUST mirror app/(main)/layout.tsx's `main` padding (fixed
        // header + measured announcement height + iOS safe-area inset) plus a
        // 0.5rem breathing gap — a static `top-24` sat behind the header on a
        // notched iPhone and behind most of it with a banner active.
        "self-start sticky top-[calc(4rem+var(--banner-h,0px)+env(safe-area-inset-top)+0.5rem)]",
        // iOS safe-area padding on the left + bottom.
        "pl-[env(safe-area-inset-left)] pb-[env(safe-area-inset-bottom)]",
        className ?? "",
      ].join(" ")}
      aria-label="Shop by quick filters"
    >
      <div
        className={[
          // h-[...] (not max-h) so the rail always uses the full
          // viewport-bounded height even when the product list is
          // short. With `max-h-`, a page with few products kept the
          // rail compressed to its content size, leaving many tabs
          // squeezed; with explicit `h-` the rail is consistently tall
          // and the inner tablist scrolls when tab count overflows.
          // dvh keeps iOS URL-bar changes from chopping the rail.
          // The `min-h-0` + `flex-1` on the tablist below is what
          // triggers overflow-y-auto when tab count exceeds the rail.
          // The height subtracts the SAME dynamic offset the sticky `top`
          // adds (4.5rem + banner + safe-area) plus a 2rem bottom gap, so
          // the rail never runs past the viewport bottom when a banner is
          // live — with a fixed 8rem it did.
          "flex h-[calc(100dvh_-_6.5rem_-_var(--banner-h,0px)_-_env(safe-area-inset-top))] flex-col overflow-hidden",
          // Card-style surface so the rail reads as a distinct nav panel.
          "rounded-2xl border-2 border-border bg-card shadow-md",
        ].join(" ")}
      >
        <div
          className="shrink-0 border-b border-border/60 px-1 py-2 min-[400px]:px-2 text-center text-[9px] font-semibold uppercase tracking-wider text-muted-foreground"
          aria-hidden="true"
        >
          Shop by
        </div>
        <div
          role="tablist"
          aria-label="Product categories"
          aria-orientation="vertical"
          aria-controls={controlsId}
          // min-h-0 is critical: without it, flex-1 won't allow the
          // tablist to shrink below its content size, so overflow-y-auto
          // never engages. With min-h-0 the tablist obeys the parent's
          // height and scrolls internally.
          // Tight padding (p-1 = 4px) maximises label width on the narrow rail
          // while still leaving room for the active tab's 3px outside-left
          // accent shadow (so overflow-x-hidden doesn't clip it).
          className="flex min-h-0 flex-1 flex-col gap-1 min-[400px]:gap-1.5 overflow-y-auto overflow-x-hidden p-1 min-[400px]:p-1.5"
        >
        {indexedTabs.map((t) => (
          <ProductQuickNavTab
            key={t.slug}
            ref={(el) => {
              tabRefs.current[t.index] = el;
            }}
            index={t.index}
            slug={t.slug}
            label={t.label}
            iconName={t.iconName}
            displayColour={t.displayColour}
            active={t.active}
            href={t.href}
            onKeyDown={handleKeyDown}
          />
        ))}
        </div>
      </div>
    </aside>
  );
}
